import { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { imgUrl, runJob } from "@/lib/glossyApi";
import {
  RefreshCw, Save, PenLine, Send, Eye, Calendar, Image as ImageIcon, Sparkles, Undo2, Images, X, Star, Upload,
} from "lucide-react";

/**
 * MemberArticlePanel — ONE panel per magazine piece, wired to the SAME article Master Build creates.
 * No hidden duplicate copies: what you see here is exactly what publishes.
 */
export default function MemberArticlePanel({
  member, kind, sectionId, tid, heading, note, publicNote,
  article, onArticleChange, categories, extraFooter,
}) {
  const m = member;
  const a = article || null;

  const [title, setTitle] = useState("");
  const [dek, setDek] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState(categories ? categories[0] : "");
  const [publishAt, setPublishAt] = useState("");
  const [editions, setEditions] = useState([]);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickMode, setPickMode] = useState("cover");
  const [brief, setBrief] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => { setNotes(a?.client_notes || ""); }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyNotes = async () => {
    if (notes.trim().length < 10) { toast.error("Paste the client's notes first."); return; }
    if (!window.confirm("Apply their notes now? The desk makes ONLY the changes they asked for — every other sentence stays word-for-word. This replaces the current draft (Undo isn't available, so copy the body first if unsure).")) return;
    setBusy("notes");
    try {
      const r = await client.post(`/admin/articles/${a.id}/apply-notes`, { notes });
      onArticleChange(r.data);
      const n = (r.data.notes_summary || []).length;
      toast.success(n ? `Done — ${n} change${n === 1 ? "" : "s"} applied, everything else untouched.` : "Their notes were applied.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply the notes."); }
    setBusy("");
  };

  useEffect(() => {
    setTitle(a?.title || ""); setDek(a?.dek || ""); setBody(a?.body || "");
    if (categories && a?.category && categories.includes(a.category)) setCategory(a.category);
    setDirty(false);
  }, [a?.id, a?.title, a?.dek, a?.body]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    client.get("/admin/editions/upcoming").then((r) => setEditions(r.data || [])).catch(() => setEditions([]));
  }, []);

  const track = (fn) => (e) => { fn(e.target.value); setDirty(true); };
  const isPublished = a?.status === "published";
  const isScheduled = a?.status === "scheduled";

  const draftWithAI = async () => {
    if (!window.confirm(`Write the ${heading} piece with AI now? It writes the copy AND shoots a fresh cover image + spread from the Master Photos. This spends AI credits${a ? " and replaces the current draft" : ""}.`)) return;
    setBusy("draft");
    try {
      const payload = categories ? { category } : {};
      if (brief.trim()) payload.brief = brief.trim();
      const r = await runJob(`/admin/members/${m.id}/article/${kind}/draft`, payload);
      onArticleChange(r.data);
      toast.success(`${heading} drafted — review below, then Publish.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed."); }
    setBusy("");
  };

  const save = async () => {
    if (!a) return;
    setBusy("save");
    try {
      const payload = { title, dek, body };
      if (categories) payload.category = category;
      const r = await client.patch(`/admin/articles/${a.id}`, payload);
      onArticleChange(r.data); setDirty(false);
      toast.success("Saved.");
    } catch { toast.error("Save failed."); }
    setBusy("");
  };

  const publishNow = async () => {
    if (!a || !title || !body) { toast.error("Nothing to publish yet — Draft with AI or run Master Build first."); return; }
    if (!window.confirm(`Publish "${title}" live now? It appears on the public magazine immediately.`)) return;
    setBusy("publish");
    try {
      const payload = { title, dek, body, status: "published" };
      if (categories) payload.category = category;
      const r = await client.patch(`/admin/articles/${a.id}`, payload);
      onArticleChange(r.data); setDirty(false);
      toast.success(`${heading} is LIVE.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed."); }
    setBusy("");
  };

  const unpublish = async () => {
    if (!window.confirm("Take this piece off the public magazine? It goes back to draft — nothing is deleted.")) return;
    setBusy("unpublish");
    try {
      const r = await client.patch(`/admin/articles/${a.id}`, { status: "draft" });
      onArticleChange(r.data);
      toast.success("Unpublished — back to draft.");
    } catch { toast.error("Failed."); }
    setBusy("");
  };

  const schedule = async () => {
    if (!publishAt) { toast.error("Pick an edition first."); return; }
    if (!a || !title || !body) { toast.error("Write the piece before scheduling."); return; }
    setBusy("schedule");
    try {
      if (dirty) {
        const payload = { title, dek, body };
        if (categories) payload.category = category;
        await client.patch(`/admin/articles/${a.id}`, payload);
      }
      const r = await client.post(`/admin/articles/${a.id}/schedule`, { publish_at: publishAt });
      onArticleChange(r.data); setDirty(false);
      toast.success(`Scheduled for the ${prettyDate(publishAt)} edition.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Schedule failed."); }
    setBusy("");
  };

  const regenImage = async () => {
    if (!window.confirm("Shoot a fresh cover image for this piece? The copy is untouched. This spends AI credits.")) return;
    setBusy("image");
    try {
      const r = await runJob(`/admin/articles/${a.id}/regenerate-image`);
      onArticleChange({ ...a, cover_image: r.data.image });
      toast.success("New cover image set. It's also saved in AI Images Saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Image failed."); }
    setBusy("");
  };

  const toggleHeadroom = async () => {
    const v = a.hero_fit === "headroom" ? "" : "headroom";
    setBusy("headroom");
    try {
      const r = await client.patch(`/admin/articles/${a.id}`, { hero_fit: v });
      onArticleChange(r.data);
      toast.success(v ? "Full headroom on — this piece only, everyone else unchanged." : "Back to the standard S/ME crop.");
    } catch { toast.error("Couldn't update."); }
    setBusy("");
  };

  const toggleContact = async () => {
    const v = !a.hide_contact;
    setBusy("contactbox");
    try {
      const r = await client.patch(`/admin/articles/${a.id}`, { hide_contact: v });
      onArticleChange(r.data);
      toast.success(v ? "Contact box hidden on this piece." : "Contact box back on this piece.");
    } catch { toast.error("Couldn't update."); }
    setBusy("");
  };

  const pickSaved = async (path) => {
    setBusy("choose");
    try {
      if (pickMode === "cover") {
        const r = await client.patch(`/admin/articles/${a.id}`, { cover_image: path });
        onArticleChange(r.data);
        toast.success("Cover image swapped — no credits spent.");
      } else {
        const r = await client.post(`/admin/articles/${a.id}/gallery/add-originals`, { paths: [path] });
        onArticleChange({ ...a, gallery: r.data.gallery });
        toast.success("Added to the spread — no credits spent.");
      }
      setShowPicker(false);
    } catch { toast.error("Couldn't set that image."); }
    setBusy("");
  };

  const deleteSpread = async (path) => {
    if (!window.confirm("Remove this image from the article spread? It stays in AI Images Saved.")) return;
    setBusy("spread");
    try {
      const r = await client.post(`/admin/articles/${a.id}/gallery/remove`, { image: path });
      onArticleChange({ ...a, gallery: r.data.gallery });
      toast.success("Removed from the spread.");
    } catch { toast.error("Couldn't remove."); }
    setBusy("");
  };

  const reshootSpread = async (path) => {
    if (!window.confirm("Reshoot this spread image with AI? The copy is untouched. This spends AI credits.")) return;
    setBusy(`reshoot-${path}`);
    try {
      const r = await client.post(`/admin/articles/${a.id}/gallery/reshoot`, { image: path });
      onArticleChange({ ...a, gallery: r.data.gallery });
      toast.success("Spread image reshot. Also saved in AI Images Saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Reshoot failed."); }
    setBusy("");
  };

  const promoteSpread = async (path) => {
    setBusy(`promote-${path}`);
    try {
      const r = await client.post(`/admin/articles/${a.id}/gallery/promote`, { image: path });
      onArticleChange({ ...a, cover_image: r.data.cover_image, gallery: r.data.gallery });
      toast.success("That's the cover now — the old cover moved into the spread. No credits spent.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't swap that."); }
    setBusy("");
  };

  const uploadImage = async (e, mode) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(`upload-${mode}`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (mode === "cover") {
        const r = await client.post(`/admin/articles/${a.id}/cover`, fd);
        onArticleChange({ ...a, cover_image: r.data.cover_image });
        toast.success("Your photo is the cover now — no AI, no credits.");
      } else {
        const r = await client.post(`/admin/articles/${a.id}/gallery-upload`, fd);
        onArticleChange({ ...a, gallery: r.data.gallery });
        toast.success("Your photo joined the spread — no AI, no credits.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — try again."); }
    setBusy(""); e.target.value = "";
  };

  const gallery = (a?.gallery || []).map((g) => (typeof g === "string" ? g : g.image)).filter(Boolean);
  const uploadedPics = [...(m.photos || []), ...(m.venue_photos || [])].map((p, i) => ({ id: `upl-${i}`, image: p, uploaded: true }));
  const savedShots = [...uploadedPics, ...[...(m.shot_gallery || [])].reverse()];

  return (
    <section id={sectionId} data-testid={`${tid}-section`} className="space-y-4 pt-4 scroll-mt-24">
      <div className="bg-ink text-cream px-5 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 grain relative">
        <span className="overline text-gloss-gold relative z-10">{heading} · {note}</span>
        <span className={`relative z-10 ml-auto font-mono text-[10px] px-2 py-0.5 ${isPublished ? "bg-green-600 text-white" : isScheduled ? "bg-gloss-gold text-ink" : a ? "bg-cream/15 text-cream/70" : "bg-cream/10 text-cream/50"}`} data-testid={`${tid}-status-pill`}>
          {isPublished ? "LIVE" : isScheduled ? "SCHEDULED" : a ? "DRAFT READY" : "EMPTY"}
        </span>
      </div>

      {!a ? (
        <div className="border border-dashed border-ink/25 bg-white p-8 text-center space-y-4" data-testid={`${tid}-empty`}>
          <p className="font-serif text-xl text-ink">Nothing written yet.</p>
          <p className="font-sans text-sm text-ink/55 max-w-xl mx-auto">
            <b>Master Build</b> (top of the Master section) fills this panel automatically — copy, cover image and spread.
            Or draft just this one piece below.
          </p>
          {categories && (
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="border border-ink/30 px-4 py-2.5 font-sans text-sm bg-white focus:outline-none focus:border-ink" data-testid={`${tid}-category`}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
            placeholder="Your brief (optional) — tell the desk exactly what this piece should be about and it writes THAT"
            className="w-full max-w-xl mx-auto block border border-ink/25 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none"
            data-testid={`${tid}-brief`} />
          <div>
            <button onClick={draftWithAI} disabled={!!busy}
              className="bg-ink text-cream px-6 py-3 overline inline-flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`${tid}-draft-btn`}>
              {busy === "draft" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} {brief.trim() ? "Draft from my brief" : "Draft this piece with AI"}
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-ink/15 bg-white" data-testid={`${tid}-card`}>
          {/* ===== Images: cover + spread ===== */}
          <div className="p-5 sm:p-6 border-b border-ink/10">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <p className="overline text-ink/60">Cover image + spread — exactly what publishes</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { setPickMode("cover"); setShowPicker(pickMode !== "cover" || !showPicker); }} disabled={!!busy}
                  className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 hover:border-ink transition-colors disabled:opacity-50" data-testid={`${tid}-choose-image-btn`}>
                  <Images size={12} /> {showPicker && pickMode === "cover" ? "Close picker" : "Swap cover (free)"}
                </button>
                <button onClick={() => { setPickMode("spread"); setShowPicker(pickMode !== "spread" || !showPicker); }} disabled={!!busy}
                  className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 hover:border-ink transition-colors disabled:opacity-50" data-testid={`${tid}-add-spread-btn`}>
                  <Images size={12} /> {showPicker && pickMode === "spread" ? "Close picker" : "Add spread image (free)"}
                </button>
                <label className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 cursor-pointer hover:border-ink transition-colors" data-testid={`${tid}-upload-cover-btn`}>
                  <Upload size={12} /> {busy === "upload-cover" ? "Uploading…" : "Upload cover"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, "cover")} data-testid={`${tid}-upload-cover-input`} />
                </label>
                <label className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 cursor-pointer hover:border-ink transition-colors" data-testid={`${tid}-upload-spread-btn`}>
                  <Upload size={12} /> {busy === "upload-spread" ? "Uploading…" : "Upload spread pic"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, "spread")} data-testid={`${tid}-upload-spread-input`} />
                </label>
                <button onClick={regenImage} disabled={!!busy}
                  className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 hover:border-ink transition-colors disabled:opacity-50" data-testid={`${tid}-regen-image-btn`}>
                  {busy === "image" ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />} New cover shot (credits)
                </button>
                <button onClick={toggleHeadroom} disabled={!!busy} title="Only affects this one piece — shifts the hero crop up so the head is never cut off"
                  className={`overline text-[10px] border px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-50 ${a.hero_fit === "headroom" ? "bg-gloss-gold border-gloss-gold text-ink" : "border-ink/30 hover:border-ink"}`} data-testid={`${tid}-headroom-btn`}>
                  {busy === "headroom" ? <RefreshCw size={12} className="animate-spin" /> : <ImageIcon size={12} />} {a.hero_fit === "headroom" ? "Full headroom: ON" : "Fix cut-off head (this piece only)"}
                </button>
                <button onClick={toggleContact} disabled={!!busy} title="Hide the 'Choose [business]' contact-details box at the bottom of this piece"
                  className={`overline text-[10px] border px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-50 ${a.hide_contact ? "bg-gloss-gold border-gloss-gold text-ink" : "border-ink/30 hover:border-ink"}`} data-testid={`${tid}-contact-toggle-btn`}>
                  {busy === "contactbox" ? <RefreshCw size={12} className="animate-spin" /> : <X size={12} />} {a.hide_contact ? "Contact box: HIDDEN" : "Hide contact box (this piece)"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="relative border-2 border-gloss-gold aspect-[3/4] bg-ink overflow-hidden" data-testid={`${tid}-cover-image`}>
                {a.cover_image ? (
                  <img src={imgUrl({ cover_image: a.cover_image })} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-cream/40">
                    <ImageIcon size={20} /><p className="overline text-[10px]">no cover yet</p>
                  </div>
                )}
                <span className="absolute top-1.5 left-1.5 bg-gloss-gold text-ink font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5">Cover</span>
              </div>
              {gallery.map((p, i) => (
                <div key={p} className="relative border border-ink/15 aspect-[3/4] bg-secondary overflow-hidden" data-testid={`${tid}-gallery-${i}`}>
                  <img src={imgUrl({ cover_image: p })} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute top-1.5 left-1.5 bg-ink/80 text-cream font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5">Spread {i + 1}</span>
                  <div className="absolute top-1 right-1 flex gap-1">
                    <button onClick={() => promoteSpread(p)} disabled={!!busy} title="Make this the cover (free — old cover moves into the spread)"
                      className="bg-ink/80 text-cream p-1.5 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid={`${tid}-spread-promote-${i}`}>
                      {busy === `promote-${p}` ? <RefreshCw size={11} className="animate-spin" /> : <Star size={11} />}
                    </button>
                    <button onClick={() => reshootSpread(p)} disabled={!!busy} title="Reshoot with AI (credits)"
                      className="bg-ink/80 text-cream p-1.5 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid={`${tid}-spread-reshoot-${i}`}>
                      {busy === `reshoot-${p}` ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
                    </button>
                    <button onClick={() => deleteSpread(p)} disabled={!!busy} title="Remove from the spread"
                      className="bg-ink/80 text-cream p-1.5 hover:bg-red-700 transition-colors disabled:opacity-50" data-testid={`${tid}-spread-delete-${i}`}>
                      <X size={11} />
                    </button>
                  </div>
                  {busy === `reshoot-${p}` && (
                    <div className="absolute inset-0 bg-ink/80 flex flex-col items-center justify-center gap-2 text-cream">
                      <RefreshCw size={16} className="animate-spin" /><p className="overline text-[9px]">reshooting…</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {showPicker && (
              <div className="mt-4 border border-ink/15 p-3" data-testid={`${tid}-image-picker`}>
                <p className="overline text-[10px] text-ink/60 mb-2">
                  {pickMode === "cover" ? "Pick any image as the cover — their uploaded photos or studio shots, free" : "Pick any image to ADD to the spread — their uploaded photos or studio shots, free"}
                </p>
                {savedShots.length === 0 ? (
                  <p className="font-mono text-[11px] text-ink/45 p-4 text-center">No saved images yet.</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 max-h-64 overflow-y-auto">
                    {savedShots.map((g) => (
                      <button key={g.id} onClick={() => pickSaved(g.image)} disabled={!!busy}
                        className={`relative aspect-[3/4] overflow-hidden border-2 transition-colors ${pickMode === "cover" && a.cover_image === g.image ? "border-gloss-gold" : "border-transparent hover:border-ink"}`}
                        data-testid={`${tid}-pick-${g.id}`}>
                        <img src={imgUrl({ cover_image: g.image })} alt="" className="w-full h-full object-cover" loading="lazy" />
                        {g.uploaded && <span className="absolute bottom-0 inset-x-0 bg-ink/80 text-cream font-mono text-[8px] uppercase tracking-wider text-center py-0.5">their photo</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== Copy ===== */}
          <div className="p-5 sm:p-6 space-y-4 border-b border-ink/10">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              {categories ? (
                <div className="min-w-[240px]">
                  <label className="overline text-[10px] text-ink/60 block mb-1">Category (drives the writing angle)</label>
                  <select value={category} onChange={(e) => { setCategory(e.target.value); setDirty(true); }}
                    className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink bg-white" data-testid={`${tid}-category`}>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ) : <div />}
              <button onClick={draftWithAI} disabled={!!busy}
                className="bg-ink text-cream px-4 py-2.5 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`${tid}-draft-btn`}>
                {busy === "draft" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} {brief.trim() ? "Rewrite from my brief" : "Rewrite with AI"}
              </button>
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Your brief (optional) — what should this piece be about?</label>
              <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
                placeholder="Type your angle, subject or key points here — the rewrite follows YOUR brief, not its own idea"
                className="w-full border border-ink/25 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none" data-testid={`${tid}-brief`} />
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Headline</label>
              <input value={title} onChange={track(setTitle)} placeholder="A magazine-worthy headline"
                className="w-full border border-ink/30 px-4 py-2.5 font-serif text-lg focus:outline-none focus:border-ink" data-testid={`${tid}-title`} />
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Standfirst</label>
              <input value={dek} onChange={track(setDek)} placeholder="A one-sentence standfirst that sells the read"
                className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid={`${tid}-dek`} />
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Body (Markdown — ## for subheads, &gt; for pull-quotes)</label>
              <textarea value={body} onChange={track(setBody)} rows={14}
                className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink leading-relaxed" data-testid={`${tid}-body`} />
              <button onClick={async () => {
                  if (!body.trim()) { toast.error("Paste or write your copy in the body box first."); return; }
                  setBusy("polish");
                  try {
                    const r = await client.post("/admin/polish", { text: body });
                    setBody(r.data.body); setDirty(true);
                    toast.success("Tidied — your words kept, just cleaned up. Press Save to keep it.");
                  } catch { toast.error("Couldn't polish — try again."); }
                  setBusy("");
                }} disabled={!!busy}
                className="mt-2 overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50"
                data-testid={`${tid}-polish-btn`}>
                {busy === "polish" ? "Polishing…" : "Polish my words — paste anything above, this only tidies it (never rewrites)"}
              </button>
              <div className="mt-4 border border-gloss-gold/50 bg-gloss-gold/5 p-3" data-testid={`${tid}-client-notes-panel`}>
                <label className="overline text-[10px] text-ink/60 block mb-1">Client notes — paste their feedback email; Apply makes ONLY those changes, the rest stays word-for-word</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                  placeholder={'e.g. "Please use my professional name throughout, remove the line about…, and update my qualification to…"'}
                  className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink leading-relaxed" data-testid={`${tid}-client-notes`} />
                <button onClick={applyNotes} disabled={!!busy || !a}
                  className="mt-2 overline text-[10px] border border-gloss-gold bg-gloss-gold/20 px-3 py-2 flex items-center gap-1.5 hover:bg-gloss-gold transition-colors disabled:opacity-50"
                  data-testid={`${tid}-apply-notes-btn`}>
                  {busy === "notes" ? "Applying their notes…" : "Apply their notes to the piece"}
                </button>
                {a?.notes_applied_at && <p className="font-mono text-[10px] text-ink/40 mt-2" data-testid={`${tid}-notes-applied-at`}>Notes last applied {new Date(a.notes_applied_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — the notes stay saved here for reference.</p>}
              </div>
            </div>
          </div>

          {/* ===== Footer: schedule + publish ===== */}
          <div className="p-5 sm:p-6 space-y-4">
            <div className="border border-ink/15 p-3 max-w-lg">
              <p className="overline text-[10px] text-ink/60 mb-2 flex items-center gap-1.5"><Calendar size={12} /> Schedule for a future edition</p>
              <div className="flex gap-2">
                <select value={publishAt} onChange={(e) => setPublishAt(e.target.value)}
                  className="flex-1 border border-ink/30 px-3 py-2 font-mono text-[12px] focus:outline-none focus:border-ink bg-white" data-testid={`${tid}-schedule-picker`}>
                  <option value="">— pick an edition —</option>
                  {editions.map((ed) => (
                    <option key={ed.starts} value={ed.starts}>{prettyDate(ed.starts)} · Issue #{ed.issue_no}</option>
                  ))}
                </select>
                <button onClick={schedule} disabled={!!busy || !publishAt}
                  className="border border-ink px-4 py-2 overline hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid={`${tid}-schedule-btn`}>
                  {busy === "schedule" ? <RefreshCw size={14} className="animate-spin" /> : "Schedule"}
                </button>
              </div>
              {isScheduled && a.publish_at && <p className="font-mono text-[10px] text-gloss-gold mt-2">Scheduled for {prettyDate(a.publish_at)}</p>}
            </div>

            {extraFooter}

            <div className="pt-4 border-t border-ink/10 flex items-center justify-between gap-3 flex-wrap">
              <p className={`font-mono text-[11px] ${dirty ? "text-gloss-gold" : "text-ink/40"}`} data-testid={`${tid}-status`}>
                {dirty ? "Unsaved changes"
                  : isPublished ? "· LIVE ON THE MAGAZINE ·"
                  : isScheduled ? `· SCHEDULED · ${prettyDate(a.publish_at)}`
                  : "Draft ready — hit Publish now to go live"}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={save} disabled={!!busy || !dirty}
                  className="border border-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid={`${tid}-save-btn`}>
                  {busy === "save" ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
                {isPublished && (
                  <>
                    <a href={`/article/${a.id}`} target="_blank" rel="noreferrer"
                      className="border border-ink/30 text-ink/70 px-4 py-2.5 overline flex items-center gap-2 hover:border-ink hover:text-ink transition-colors" data-testid={`${tid}-view-live`}>
                      <Eye size={14} /> View live
                    </a>
                    <button onClick={unpublish} disabled={!!busy}
                      className="border border-ink/30 text-ink/70 px-4 py-2.5 overline flex items-center gap-2 hover:border-red-600 hover:text-red-600 transition-colors disabled:opacity-40" data-testid={`${tid}-unpublish-btn`}>
                      {busy === "unpublish" ? <RefreshCw size={14} className="animate-spin" /> : <Undo2 size={14} />} Unpublish
                    </button>
                  </>
                )}
                <button onClick={publishNow} disabled={!!busy || !title || !body}
                  className="bg-gloss-gold text-ink px-6 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`${tid}-publish-now-btn`}>
                  {busy === "publish" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} {isPublished ? "Re-publish" : "Publish now"}
                </button>
              </div>
            </div>
            {publicNote && <p className="font-mono text-[10px] text-ink/40 text-right">{publicNote}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function prettyDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}
